import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ReimbursementRequest, ReimbursementRequest_DTO } from '@models/request';
import { mapReimbursementRequest_DTO_To_ReimbursementRequest } from '@services/transforms/request-transform';
import { convertToFormData } from '@utility/formData-util';
import {
    getSiteBaseUrl,
    isInProcessOrHigher_PaymentRequest,
    isTypeMileageExpense,
    SaveStatusResponse,
} from '@utility/utility';
import { BehaviorSubject, map, shareReplay, switchMap, tap } from 'rxjs';

@Injectable({
    providedIn: 'any', // must be provided page-wise, not a singleton, because it provides state that should be cleared page changes
})
export class ReimbursementRequestService {
    private http = inject(HttpClient);
    private readonly subject = new BehaviorSubject<ReimbursementRequest | undefined>(undefined);
    public readonly state$ = this.subject.asObservable(); //.pipe(skip(1), shareReplay(1));
    public readonly isInProcessOrHigher$ = this.state$.pipe(
        map((request) => {
            if (!request) return null;
            return isInProcessOrHigher_PaymentRequest(request.status?.statusId);
        }),
        shareReplay(1)
    );
    get(requestId: number) {
        return this.http
            .get<ReimbursementRequest_DTO>(`${getSiteBaseUrl()}/api/requests/reimbursement/${requestId}`)
            .pipe(
                map((dto) => mapReimbursementRequest_DTO_To_ReimbursementRequest(dto)),
                map((request) => {
                    // data migration, some expenses have fields from different type which causes problems
                    // e.g. non-mileage expense that has its expense.manualTotalCalculation = true - this is mileage-only field
                    // and will cause incorrect behavior when changing expense types

                    request?.reimbursementTransactions.forEach((expense) => {
                        if (isTypeMileageExpense(expense.expenseId)) {
                        } else {
                            expense.manualTotalCalculation = undefined;
                            expense.mileageComments = undefined;
                            expense.mileageDistanceUnits = undefined;
                            expense.mileageStartLocation = undefined;
                            expense.mileageEndLocation = undefined;
                            expense.mileageReimbursementCountry = undefined;
                            expense.mileageReimbursementRatePerUnit = undefined;
                            expense.mileageTotalDistance = undefined;
                        }
                    });

                    return request;
                }),
                tap((request) => this.subject.next(request))
            );
    }
    getStateValue() {
        return this.subject.getValue();
    }

    set(request: ReimbursementRequest): void {
        this.subject.next(request);
    }

    clearState() {
        this.subject.next(undefined);
    }

    create(request: ReimbursementRequest) {
        const formData = convertToFormData(request);
        return this.http.post<SaveStatusResponse>(`${getSiteBaseUrl()}/api/requests/reimbursement`, formData);
    }

    update(requestId: number, request: ReimbursementRequest) {
        const formData = convertToFormData(request);
        return this.http
            .put<SaveStatusResponse>(`${getSiteBaseUrl()}/api/requests/reimbursement/${requestId}`, formData)
            .pipe(
                switchMap(() => {
                    return this.get(requestId);
                })
            );
    }

    getAttachmentUri(input: { requestId: number; attachmentId: number }) {
        return new URL(
            `${getSiteBaseUrl()}/api/requests/reimbursement/${input.requestId}/attachment/${input.attachmentId}`
        );
    }
}
